import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Chip, Grid, Alert, CircularProgress,
  Avatar, Tooltip, IconButton, LinearProgress, Divider, List,
  ListItem, ListItemText, ListItemIcon,
} from '@mui/material';
import {
  Dashboard, Refresh, People, AttachMoney, TrendingUp,
  TrendingDown, Warning, CheckCircle, FiberManualRecord,
} from '@mui/icons-material';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';

function apiFetch(path: string) {
  return fetch(`${API}${path}`, {
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

function fmt(n: number | string, dec = 0) {
  return parseFloat(String(n ?? 0)).toLocaleString('en-HK', { minimumFractionDigits: dec, maximumFractionDigits: dec });
}
function fmtHKD(n: number | string) {
  const val = parseFloat(String(n ?? 0));
  if (val >= 1_000_000) return `HK$${(val / 1_000_000).toFixed(1)}M`;
  if (val >= 1_000) return `HK$${(val / 1_000).toFixed(0)}K`;
  return `HK$${fmt(val)}`;
}

function KpiCard({ label, value, sub, color = '#37474f', icon, trend, onClick }: any) {
  return (
    <Paper sx={{ p: 2, borderTop: `3px solid ${color}`, cursor: onClick ? 'pointer' : 'default',
      '&:hover': onClick ? { boxShadow: 3 } : {} }} onClick={onClick}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" fontWeight={800} color={color}>{value ?? '—'}</Typography>
          <Typography variant="caption" color="text.secondary">{label}</Typography>
          {sub && <Typography variant="caption" display="block" color="text.disabled" sx={{ mt: 0.25 }}>{sub}</Typography>}
        </Box>
        <Box sx={{ color: `${color}60`, ml: 1 }}>{icon}</Box>
      </Box>
      {trend !== undefined && (
        <Box sx={{ mt: 0.75, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {trend >= 0 ? <TrendingUp sx={{ fontSize: 14, color: '#2e7d32' }} /> : <TrendingDown sx={{ fontSize: 14, color: '#b71c1c' }} />}
          <Typography variant="caption" color={trend >= 0 ? '#2e7d32' : '#b71c1c'} fontWeight={600}>
            {trend >= 0 ? '+' : ''}{trend}% vs last month
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

function MiniBar({ label, value, max, color }: { label: string; value: number; max: number; color: string }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <Box sx={{ mb: 0.75 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.15 }}>
        <Typography variant="caption">{label}</Typography>
        <Typography variant="caption" fontWeight={700}>{value}</Typography>
      </Box>
      <LinearProgress variant="determinate" value={Math.min(pct, 100)}
        sx={{ height: 5, borderRadius: 2, '& .MuiLinearProgress-bar': { bgcolor: color } }} />
    </Box>
  );
}

const EVENT_COLORS: Record<string, string> = {
  'Probation End':    '#f57f17',
  'Contract Renewal': '#e65100',
  'Doc Expiry':       '#b71c1c',
  'Work Anniversary': '#2e7d32',
};

export default function ExecutiveDashboard() {
  const [data, setData]       = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { setData(await apiFetch('/api/executive-dashboard')); }
    catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const hc   = data?.headcount ?? {};
  const cost = data?.cost ?? {};
  const reqs = data?.openReqs ?? {};
  const eng  = data?.engagement ?? {};
  const risk = data?.turnoverRisk ?? {};
  const comp = data?.compliance ?? {};
  const div  = data?.diversity ?? {};
  const pend = data?.pendingApprovals ?? {};
  const movement: any[] = data?.movement ?? [];
  const payroll: any[]  = data?.payrollSpend ?? [];
  const upcoming: any[] = data?.upcoming ?? [];

  const lastPayroll  = payroll[payroll.length - 1];
  const prevPayroll  = payroll[payroll.length - 2];
  const payrollTrend = lastPayroll && prevPayroll
    ? Math.round(((parseFloat(lastPayroll.gross) - parseFloat(prevPayroll.gross)) / parseFloat(prevPayroll.gross)) * 100)
    : undefined;

  const lastMonth = movement[movement.length - 1];
  const netChange = lastMonth ? (parseInt(lastMonth.joiners) - parseInt(lastMonth.leavers)) : 0;

  const compIssues = (parseInt(comp.expired ?? 0) + parseInt(comp.critical ?? 0));
  const highRisk   = parseInt(risk.highRisk ?? 0);

  // Attrition this month
  const attritionPct = hc.total > 0 && lastMonth
    ? ((parseInt(lastMonth.leavers ?? 0) / hc.total) * 100).toFixed(1)
    : '0.0';

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: '#37474f', width: 52, height: 52 }}><Dashboard /></Avatar>
          <Box>
            <Typography variant="h5" fontWeight={700}>Executive People Dashboard</Typography>
            <Typography variant="body2" color="text.secondary">
              Strategic HR overview — {data?.generatedAt ? new Date(data.generatedAt).toLocaleString('en-HK', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
            </Typography>
          </Box>
        </Box>
        <Tooltip title="Refresh"><IconButton onClick={load} disabled={loading}><Refresh /></IconButton></Tooltip>
      </Box>

      {/* Alert bar */}
      {(compIssues > 0 || highRisk > 0 || parseInt(pend.total ?? 0) > 10) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {compIssues > 0 && <><strong>{compIssues} compliance doc issues</strong> (expired/expiring). </>}
          {highRisk > 0 && <><strong>{highRisk} employees</strong> at high turnover risk. </>}
          {parseInt(pend.total ?? 0) > 10 && <><strong>{pend.total} pending approvals</strong> awaiting action.</>}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
      ) : (
        <>
          {/* Row 1: Core metrics */}
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <KpiCard label="Total Headcount" value={fmt(hc.total)}
                sub={`${hc.departments} departments`} color="#1565c0" icon={<People />} />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <KpiCard label="Monthly Payroll" value={fmtHKD(cost.monthlyPayroll)}
                color="#C08A2E" icon={<AttachMoney />} trend={payrollTrend} />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <KpiCard label="Net Movement (mo)"
                value={netChange >= 0 ? `+${netChange}` : String(netChange)}
                sub={`+${lastMonth?.joiners ?? 0} joined, -${lastMonth?.leavers ?? 0} left`}
                color={netChange >= 0 ? '#2e7d32' : '#b71c1c'} />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <KpiCard label="Monthly Attrition" value={`${attritionPct}%`}
                color={parseFloat(attritionPct) > 2 ? '#b71c1c' : '#2e7d32'} />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <KpiCard label="Open Reqs" value={fmt(reqs.open)}
                sub={`${reqs.stale ?? 0} stale (>60d)`}
                color={parseInt(reqs.stale) > 3 ? '#e65100' : '#37474f'} />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <KpiCard label="Wellness Score" value={eng.wellnessScore ? `${parseFloat(eng.wellnessScore).toFixed(0)}/100` : '—'}
                sub={`${eng.checkinCount ?? 0} check-ins (30d)`}
                color={parseFloat(eng.wellnessScore) >= 70 ? '#2e7d32' : parseFloat(eng.wellnessScore) >= 50 ? '#f57f17' : '#b71c1c'} />
            </Grid>
          </Grid>

          {/* Row 2: Three panels */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {/* Headcount mix */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>Workforce Mix</Typography>
                <MiniBar label="Full-time" value={parseInt(hc.fullTime ?? 0)} max={parseInt(hc.total ?? 1)} color="#1565c0" />
                <MiniBar label="Part-time" value={parseInt(hc.partTime ?? 0)} max={parseInt(hc.total ?? 1)} color="#6a1b9a" />
                <MiniBar label="Contract"  value={parseInt(hc.contract ?? 0)} max={parseInt(hc.total ?? 1)} color="#e65100" />
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>Gender</Typography>
                <MiniBar label="Male"   value={parseInt(div.male ?? 0)} max={parseInt(hc.total ?? 1)} color="#1565c0" />
                <MiniBar label="Female" value={parseInt(div.female ?? 0)} max={parseInt(hc.total ?? 1)} color="#c2185b" />
                {parseInt(div.nationalities ?? 0) > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {div.nationalities} nationalities · avg age {div.avgAge ? parseFloat(div.avgAge).toFixed(0) : '—'}
                  </Typography>
                )}
              </Paper>
            </Grid>

            {/* Risk summary */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>Risk Indicators</Typography>
                {[
                  { label: 'High Turnover Risk', value: fmt(risk.highRisk), total: fmt(risk.total),
                    color: parseInt(risk.highRisk) > 0 ? '#b71c1c' : '#2e7d32' },
                  { label: 'Compliance Issues', value: fmt(compIssues), total: fmt((parseInt(comp.expired ?? 0) + parseInt(comp.critical ?? 0) + parseInt(comp.warning ?? 0) + parseInt(comp.valid ?? 0))),
                    color: compIssues > 0 ? '#e65100' : '#2e7d32' },
                  { label: 'Pending Approvals', value: fmt(pend.total), total: '',
                    color: parseInt(pend.total) > 10 ? '#f57f17' : '#37474f' },
                  { label: 'Stale Open Reqs (>60d)', value: fmt(reqs.stale), total: fmt(reqs.open),
                    color: parseInt(reqs.stale) > 0 ? '#e65100' : '#2e7d32' },
                ].map(r => (
                  <Box key={r.label} sx={{ mb: 1, p: 1, borderLeft: `3px solid ${r.color}`, bgcolor: r.color + '08', borderRadius: '0 4px 4px 0' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption">{r.label}</Typography>
                      <Typography variant="caption" fontWeight={700} color={r.color}>
                        {r.value}{r.total ? `/${r.total}` : ''}
                      </Typography>
                    </Box>
                  </Box>
                ))}
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>Pending Approvals</Typography>
                {[
                  { k: 'Leave', v: pend.leave }, { k: 'Expense', v: pend.expense },
                  { k: 'Overtime', v: pend.overtime }, { k: 'WFH', v: pend.wfh },
                ].map(p => p.v > 0 ? (
                  <Box key={p.k} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" color="text.secondary">{p.k}</Typography>
                    <Chip size="small" label={p.v} sx={{ height: 16, fontSize: '0.55rem' }} />
                  </Box>
                ) : null)}
              </Paper>
            </Grid>

            {/* 6-month movement */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>Headcount Movement (6 months)</Typography>
                {/* Simple bar chart */}
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-end', height: 80, mb: 1 }}>
                  {movement.map((m: any) => {
                    const maxVal = Math.max(...movement.map(x => Math.max(x.joiners ?? 0, x.leavers ?? 0)), 1);
                    const jH = Math.max(3, ((m.joiners ?? 0) / maxVal) * 72);
                    const lH = Math.max(3, ((m.leavers ?? 0) / maxVal) * 72);
                    const net = (m.joiners ?? 0) - (m.leavers ?? 0);
                    return (
                      <Box key={m.month} sx={{ flex: 1, display: 'flex', gap: 0.25, alignItems: 'flex-end', justifyContent: 'center' }}>
                        <Tooltip title={`${m.label}: +${m.joiners} joined`}>
                          <Box sx={{ flex: 1, height: jH, bgcolor: '#2e7d32', opacity: 0.8, borderRadius: '2px 2px 0 0' }} />
                        </Tooltip>
                        <Tooltip title={`${m.label}: -${m.leavers} left`}>
                          <Box sx={{ flex: 1, height: lH, bgcolor: '#b71c1c', opacity: 0.7, borderRadius: '2px 2px 0 0' }} />
                        </Tooltip>
                      </Box>
                    );
                  })}
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  {movement.map((m: any) => (
                    <Typography key={m.month} variant="caption" sx={{ flex: 1, textAlign: 'center', fontSize: '0.55rem', color: 'text.secondary' }}>
                      {m.label?.split(' ')[0]}
                    </Typography>
                  ))}
                </Box>
                <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                  <Typography variant="caption"><Box component="span" sx={{ bgcolor: '#2e7d32', px: 1, mr: 0.5, borderRadius: 0.5 }} />Joiners</Typography>
                  <Typography variant="caption"><Box component="span" sx={{ bgcolor: '#b71c1c', px: 1, mr: 0.5, borderRadius: 0.5 }} />Leavers</Typography>
                </Box>

                {/* Payroll spend trend */}
                {payroll.length > 0 && (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    <Typography variant="subtitle2" fontWeight={700} gutterBottom>Payroll Spend Trend</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-end', height: 48 }}>
                      {payroll.map((p: any) => {
                        const maxG = Math.max(...payroll.map((x: any) => parseFloat(x.gross ?? 0)), 1);
                        const h = Math.max(4, (parseFloat(p.gross ?? 0) / maxG) * 42);
                        return (
                          <Tooltip key={p.month} title={`${p.label}: ${fmtHKD(p.gross)} (${p.headcount} staff)`}>
                            <Box sx={{ flex: 1, height: h, bgcolor: '#C08A2E', opacity: 0.75, borderRadius: '2px 2px 0 0' }} />
                          </Tooltip>
                        );
                      })}
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.25 }}>
                      {payroll.map((p: any) => (
                        <Typography key={p.month} variant="caption" sx={{ flex: 1, textAlign: 'center', fontSize: '0.55rem', color: 'text.secondary' }}>
                          {p.label?.split(' ')[0]}
                        </Typography>
                      ))}
                    </Box>
                  </>
                )}
              </Paper>
            </Grid>
          </Grid>

          {/* Row 3: Upcoming events */}
          {upcoming.length > 0 && (
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                ⏰ Upcoming Actions (next 14–30 days)
              </Typography>
              <Grid container spacing={1}>
                {upcoming.map((ev: any, i: number) => {
                  const color = EVENT_COLORS[ev.type] ?? '#546e7a';
                  return (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={i}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1,
                        bgcolor: color + '10', borderRadius: 1, borderLeft: `3px solid ${color}` }}>
                        <FiberManualRecord sx={{ fontSize: 8, color }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="caption" fontWeight={700} color={color}>{ev.type}</Typography>
                          <Typography variant="body2" noWrap>{ev.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{ev.dept} · {ev.date?.slice(0, 10)}</Typography>
                        </Box>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>
          )}
        </>
      )}
    </Box>
  );
}
